import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useCallback, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import CareSearchFilters, {
  CareType,
  getFiltersForCareType,
} from './CareSearchFilters';
import CareProviderCard from '@/pwa/molecules/CareProviderCard/CareProviderCard';
import { AddToShortlistMenuButton } from '@/pwa/pages/AdminCareCircleView/AdminProviderDetail';
import MapView from './MapView';
import { Shortlist, ShortlistItemProvider } from '@karehero/models';
import { formatProviderDistance } from '@karehero/helpers/provider';
import Select from '@mui/material/Select';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';

const SORT_OPTIONS = [
  'Most relevant',
  'Nearest',
  'Highest rating',
  'Lowest price',
  'Highest price',
] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

export const DEFAULT_SORT: SortOption = 'Nearest';

const VIEW_TYPES = [
  { id: 'list', label: 'List' },
  { id: 'map', label: 'Map' },
];

type ViewType = 'list' | 'map';

interface CareProviderResultsControlProps {
  resultCount: number;
  sort: SortOption;
  setSort: (val: SortOption) => void;
  view: ViewType;
  setView: (val: ViewType) => void;
  showMapToggle?: boolean;
}
const CareProviderResultsControl = ({
  resultCount,
  sort,
  setSort,
  view,
  setView,
  showMapToggle = true,
}: CareProviderResultsControlProps) => (
  <Stack
    direction='row'
    sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Typography variant='subtitle2'>
      Displaying {resultCount} results
    </Typography>
    <Stack
      direction='row'
      spacing={1.5}
      sx={{
        alignItems: 'center',
      }}
    >
      <Select
        value={sort}
        size='xsmall'
        onChange={(ev) => setSort(ev.target.value as SortOption)}
        sx={{ width: 196 }}
        renderValue={() => `Sort: ${sort}`}
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
      {showMapToggle && (
        <ToggleButtonGroup
          size='xsmall'
          value={[view]}
          onClick={(ev: any) => setView(ev.target.value)}
        >
          {VIEW_TYPES.map((item) => (
            <ToggleButton value={item.id} aria-label={item.id}>
              {item.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Stack>
  </Stack>
);

interface CareProviderResultsGridProps {
  results: ShortlistItemProvider[];
  onProviderClick: (providerID: string) => void;
  onAddNote: (providerID: string) => void;
  shortlists?: Shortlist[];
  selectedShortlistID?: string | null;
  onAddToShortlist?: (providerID: string, shortlistId: string) => void;
  onCreateShortlist?: () => void;
}

const CareProviderResultsGrid = ({
  results,
  onProviderClick,
  onAddNote,
  shortlists,
  selectedShortlistID,
  onAddToShortlist,
  onCreateShortlist,
}: CareProviderResultsGridProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: 2,
    }}
  >
    {results.map((provider) => (
      <CareProviderCard
        key={provider.regulatoryId}
        provider={provider}
        distance={formatProviderDistance(provider.distanceMiles)}
        onClick={() => onProviderClick(provider.regulatoryId)}
        onAddNote={() => onAddNote(provider.regulatoryId)}
        actionButton={
          shortlists && onAddToShortlist && onCreateShortlist ? (
            <AddToShortlistMenuButton
              shortlists={shortlists}
              alreadyOnShortlists={shortlists.filter((s) =>
                s.regulatoryIds?.find(
                  (regID) => regID === provider.regulatoryId,
                ),
              )}
              selectedShortlistID={selectedShortlistID}
              onAdd={(shortlistId) =>
                onAddToShortlist(provider.regulatoryId, shortlistId)
              }
              onCreateNew={onCreateShortlist}
            />
          ) : undefined
        }
      />
    ))}
  </Box>
);

interface CareProviderResultsProps {
  results: ShortlistItemProvider[];
  onProviderClick: (providerID: string) => void;
  onAddNote: (providerID: string) => void;
  shortlists?: Shortlist[];
  selectedShortlistID?: string | null;
  onAddToShortlist?: (providerID: string, shortlistId: string) => void;
  onCreateShortlist?: () => void;
  onSearch?: (location: string) => void;
  onFilterChange?: (filters: Record<string, string>) => void;
  onCareTypeChange?: (careType: CareType) => void;
  onSortChange?: (sort: string) => void;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  apiKey?: string;
  searchLocation?: { lat: number; lon: number } | null;
}

export const CareProviderResults = ({
  results,
  onProviderClick,
  onAddNote,
  shortlists,
  selectedShortlistID,
  onAddToShortlist,
  onCreateShortlist,
  onSearch,
  onFilterChange,
  onCareTypeChange,
  onSortChange,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  apiKey,
  searchLocation,
}: CareProviderResultsProps) => {
  const [view, setView] = useState<'list' | 'map'>('list');
  const [sort, setSortInternal] = useState<SortOption>(DEFAULT_SORT);
  const [careType, setCareTypeInternal] = useState<CareType>('careHome');
  const [filters, setFiltersInternal] = useState<Record<string, string>>({
    distance: 'Within 5 miles',
  });
  const [location, setLocation] = useState<string>('');

  const setCareType = useCallback(
    (ct: CareType) => {
      setCareTypeInternal(ct);
      setFiltersInternal({});
      onCareTypeChange?.(ct);
      onFilterChange?.({});
    },
    [onCareTypeChange, onFilterChange],
  );

  const setFilter = useCallback(
    (id: string, value: string) => {
      const next = { ...filters, [id]: value };
      setFiltersInternal(next);
      onFilterChange?.(next);
    },
    [filters, onFilterChange],
  );

  const clearFilters = useCallback(() => {
    const empty = Object.fromEntries(
      getFiltersForCareType(careType).map((f) => [f.id, '']),
    );
    setFiltersInternal(empty);
    onFilterChange?.(empty);
  }, [onFilterChange, careType]);

  const setSort = useCallback(
    (s: SortOption) => {
      setSortInternal(s);
      onSortChange?.(s);
    },
    [onSortChange],
  );

  const showMap = view === 'map' && !!apiKey;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        gap: 3,
        mt: 3,
        overflow: 'hidden',
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'auto',
          pr: 1,
        }}
      >
        <Stack spacing={2}>
          <Stack spacing={1}>
            <CareSearchFilters
              careType={careType}
              onCareTypeChange={setCareType}
              filters={filters}
              setFilter={setFilter}
              clearFilters={clearFilters}
              location={location}
              setLocation={setLocation}
              onSearch={onSearch}
            />
          </Stack>
          <CareProviderResultsControl
            resultCount={results.length}
            sort={sort}
            setSort={setSort}
            view={view}
            setView={setView}
            showMapToggle={!!apiKey}
          />
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : showMap ? (
            <APIProvider apiKey={apiKey}>
              <Box
                sx={{
                  height: 600,
                }}
              >
                <MapView
                  results={results}
                  searchLocation={searchLocation}
                  onProviderClick={onProviderClick}
                />
              </Box>
            </APIProvider>
          ) : (
            <CareProviderResultsGrid
              results={results}
              onProviderClick={onProviderClick}
              onAddNote={onAddNote}
              shortlists={shortlists}
              selectedShortlistID={selectedShortlistID}
              onAddToShortlist={onAddToShortlist}
              onCreateShortlist={onCreateShortlist}
            />
          )}
          {hasMore && !isLoading && !showMap && onLoadMore && (
            <Button
              variant='outlined'
              onClick={onLoadMore}
              loading={isLoadingMore}
              sx={{ alignSelf: 'center' }}
            >
              Load more...
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default CareProviderResults;
